import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import React, { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious } from '_hooks';
import { selectAccessToken, selectUserId } from '_store/chat/selector';
import { selectSearchVisible } from '_store/chat/selector';
import { getChannel } from '_store/chat/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';
import ChatMessageSearchModal from 'components/composed/modals/ChatMessageSearchModal';

import CustomGroupChannel from './CustomGroupChannel';
import CustomGroupChannelList from './CustomGroupChannelList';
import CustomSearchPannel from './CustomSearchPannel';
import SendbirdSubscription from './SendbirdSubscription';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.small}px;
`;

const StyledBody = styled.div`
  .sendbird-channel-list {
    width: 100%;
  }

  .sendbird-conversation {
    flex-grow: 1;
    height: 480px;
    scrollbar-color: ${theme.colors.gray.secondary} ${theme.colors.linkWater};

    border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
    border-radius: 16px;

    .sendbird-conversation__messages {
      .sendbird-conversation__scroll-container {
        /* max-height: 500px; */

        .sendbird-conversation__messages-padding {
          padding-left: ${theme.spaces.regular}px;
          padding-right: ${theme.spaces.regular}px;
        }
      }

      .sendbird-separator {
        padding-top: ${theme.spaces.regular}px;
        padding-bottom: ${theme.spaces.regular}px;

        .sendbird-separator__text {
          .sendbird-label.sendbird-label--caption-2.sendbird-label--color-onbackground-2 {
            font-weight: 400;
          }
        }
      }

      .sendbird-admin-message {
        padding-top: ${theme.spaces.regular}px;
        padding-bottom: ${theme.spaces.regular}px;
      }

      .sendbird-msg-hoc {
        .sendbird-avatar-img--default {
          background-color: ${theme.colors.indigo.light};
        }

        .sendbird-message-content {
          padding-top: ${theme.spaces.smallX}px;
          padding-bottom: ${theme.spaces.smallX}px;

          &.selected-search-message {
            background-color: var(--sendbird-highlight-100);
          }
        }

        .sendbird-message-content.outgoing {
          .sendbird-text-message-item-body {
            .sendbird-label {
              color: ${theme.colors.white};
            }
          }

          .sendbird-voice-message-item-body {
            .sendbird-voice-message-item-body__status-button {
              .sendbird-icon {
                display: flex;
                justify-content: center;
                align-items: center;
              }
            }
          }
        }

        .sendbird-message-content-reactions {
          .sendbird-context-menu {
            .sendbird-emoji-reactions__add-reaction-badge {
              .sendbird-icon {
                display: flex;
                justify-content: center;
                align-items: center;
              }
            }
          }
        }

        &:hover {
          .sendbird-message-content.outgoing {
            .sendbird-voice-message-item-body {
              .sendbird-progress-bar.sendbird-voice-message-item-body__progress-bar.progress-bar-color--primary {
                background-color: ${hexToRGBA(theme.colors.indigo.primary, 0.8)};
              }
            }
          }
        }
      }
    }

    .sendbird-conversation__footer {
      position: sticky;
      bottom: 0;
      padding-top: ${theme.spaces.regular}px;
      padding-bottom: ${theme.spaces.regular}px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
      background-color: ${theme.colors.white};

      .sendbird-message-input-wrapper {
        margin-bottom: ${theme.spaces.smallX}px;

        .sendbird-message-input-wrapper__message-input {
          padding-left: ${theme.spaces.regular}px;
          padding-right: ${theme.spaces.regular}px;
        }

        #sendbird-message-input-text-field {
          border-radius: 12px;
        }

        .sendbird-message-input {
          .sendbird-message-input--textarea {
            overflow-y: auto;
            border-radius: 12px;
            border-width: 2px;

            &::-webkit-scrollbar {
              width: 12px;
            }

            &::-webkit-scrollbar-track {
              background: ${hexToRGBA(theme.colors.gray.secondary, 0.1)} 0% 0% no-repeat padding-box;
              border-radius: 6px;
            }

            &::-webkit-scrollbar-thumb {
              background: ${hexToRGBA(theme.colors.gray.secondary, 0.5)} 0% 0% no-repeat padding-box;
              border-radius: 6px;
            }
          }

          .sendbird-message-input--placeholder {
            top: 20px;
          }

          .sendbird-iconbutton {
            .sendbird-icon {
              display: flex;
              justify-content: center;
              align-items: center;
            }
          }
        }

        .sendbird-mention-suggest-list {
          width: auto;
          bottom: -12px;
          margin-left: ${theme.spaces.regular}px;
          margin-right: ${theme.spaces.regular}px;
          padding: ${theme.spaces.regular}px;
          overflow: auto;
          border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;

          .sendbird-mention-suggest-list__user-item {
            cursor: pointer;

            .sendbird-avatar-img--default {
              background-color: ${theme.colors.indigo.light};
            }

            .focused {
              background-color: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
            }

            * {
              font-weight: 400;
            }
          }
        }
      }

      .sendbird-message-input-wrapper--voice-message {
        .sendbird-voice-message-input {
          padding-left: ${theme.spaces.regular}px;
          padding-right: ${theme.spaces.regular}px;

          .sendbird-voice-message-input__indicator {
            .sendbird-voice-message-input__indicator__progress-bar {
              .sendbird-progress-bar.sendbird-voice-message-input__indicator__progress-bar__bar.progress-bar-color--primary {
                background-color: ${theme.colors.gray.secondary};

                .sendbird-progress-bar__fill {
                  background-color: ${hexToRGBA(theme.colors.indigo.primary, 0.8)};
                }
              }
            }

            .sendbird-voice-message-input__indicator__on-rec {
              margin-right: 4px;
            }

            .sendbird-voice-message-input__indicator__playback-time {
              display: flex;
              align-items: center;
              top: 0;
              bottom: 0;

              .sendbird-label {
                color: ${theme.colors.white};
              }
            }
          }

          .sendbird-voice-message-input__controler {
            .sendbird-icon {
              display: flex;
              justify-content: center;
              align-items: center;
            }
          }
        }
      }
    }
  }

  .sendbird-message-search-pannel {
    min-width: 300px;

    .sendbird-icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .sendbird-ui-header {
      &.sendbird-message-search-pannel__header {
        height: auto;
        min-height: unset;
        padding-bottom: ${theme.spaces.smallX}px;

        .sendbird-ui-header__middle__title.sendbird-label {
          &.sendbird-label--h-2 {
            &.sendbird-label--color-onbackground-1 {
              color: ${theme.colors.gray.primary};
            }
          }
        }

        .sendbird-icon {
          &.sendbird-icon-close {
            &.sendbird-icon-color--on-background-1 {
              svg {
                path {
                  fill: ${theme.colors.gray.primary};
                }
              }
            }
          }
        }
      }
    }

    .sendbird-message-search-pannel__input__container {
      border: 2px solid ${theme.colors.gray.secondary} !important;
      border-radius: 12px;
      box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);

      .sendbird-message-search-pannel__input__container__search-icon {
        left: ${theme.spaces.smallX}px !important;
      }

      .sendbird-message-search-pannel__input__container__input-area {
        top: 10px;
        left: 38px;
        width: calc(100% - 80px);
      }

      .sendbird-message-search-pannel__input__container__reset-input-button {
        top: 10px;
        right: 10px !important;
      }

      .sendbird-message-search-pannel__input__container__spinner {
        top: 10px;
        right: 10px !important;
      }
    }

    .sendbird-message-search {
      overflow-y: auto;

      .sendbird-message-search-item {
        &.sendbird-message-search-item--selected {
          background-color: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
        }

        .sendbird-message-search-item__left {
          border-inline-start: none;

          .sendbird-avatar-img--default {
            background-color: ${theme.colors.indigo.light};
          }
        }
      }
    }
  }

  @media ${theme.devices.tablet} {
    .col-md-4 {
      margin-bottom: ${theme.spaces.regular}px;
    }
  }
`;

const StyledChatContainer = styled.div`
  display: flex;
  gap: ${theme.spaces.regular}px;
`;

const customColorSet = {
  '--sendbird-light-onlight-01': theme.colors.gray.secondary,
  '--sendbird-light-onlight-02': theme.colors.gray.secondary,
  '--sendbird-light-onlight-03': theme.colors.gray.secondary,
  '--sendbird-light-onlight-04': hexToRGBA(theme.colors.gray.secondary, 0.16),
  '--sendbird-light-background-100': hexToRGBA(theme.colors.gray.secondary, 0.08),
  '--sendbird-light-background-200': hexToRGBA(theme.colors.gray.secondary, 0.16),
  '--sendbird-light-background-300': theme.colors.gray.secondary,
  // '--sendbird-light-primary-100': theme.colors.indigo.superLight,
  '--sendbird-light-primary-300': theme.colors.indigo.light,
  '--sendbird-light-primary-400': hexToRGBA(theme.colors.indigo.primary, 0.8),
  '--sendbird-highlight-100': hexToRGBA(theme.colors.yellow.highlight, 0.5),
};

const ChatView = () => {
  // const ref = useRef();
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const userId = useSelector(selectUserId);
  const accessToken = useSelector(selectAccessToken);
  const searchVisible = useSelector(selectSearchVisible);

  useEffect(() => {
    if (customerId && productId && prevProductId !== productId) {
      dispatch(getChannel({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  return (
    <Card>
      <StyledHeader>
        <Text size={theme.fontSizes.medium} weight={600}>
          Chat with Team
        </Text>
      </StyledHeader>
      <StyledBody>
        <SendbirdProvider
          appId={config.sendbirdAppId}
          userId={userId}
          accessToken={accessToken}
          breakpoint={isMobile}
          colorSet={customColorSet}
          uikitOptions={{
            groupChannel: { enableMention: true, enableTypingIndicator: true, enableVoiceMessage: true },
            groupChannelSettings: { enableMessageSearch: true },
          }}
          sdkInitParams={{ localCacheEnabled: false }}
        >
          <Row>
            <Col xs={12} md={searchVisible ? 3 : 4}>
              <CustomGroupChannelList />
            </Col>
            <Col xs={12} md={searchVisible ? 9 : 8}>
              <StyledChatContainer>
                <CustomGroupChannel />
                {searchVisible && <CustomSearchPannel />}
              </StyledChatContainer>
            </Col>
          </Row>
          <ChatMessageSearchModal />
          <SendbirdSubscription />
        </SendbirdProvider>
      </StyledBody>
    </Card>
  );
};

export default ChatView;
